import { Stack, TextField, TextFieldProps } from '@ds/coolshop';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FieldProps } from '@src/assets/types';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { Control, useController, useFormContext } from 'react-hook-form';

import { useCGTLocalizationProviderProps } from '../helpers/useCGTLocalizationProviderProps';
import { dateToString } from './dateToString';

export { dateToString };

interface DatePickerProps {
    name: string;
    label: string;
    control: Control<any>;
    minDate?: Dayjs;
    readOnly?: boolean;
    disabled?: boolean;
}

function DatePickerElement({
    name,
    label,
    control,
    minDate,
    readOnly = false,
    disabled = false,
    ...rest
}: DatePickerProps) {
    const { field } = useController({
        name,
        control,
    });

    // An emptied or unparseable picker means "no date": store the empty string the rest of the app
    // reads as unset, instead of the "NaN-NaN-NaN" dateToString would produce for an invalid date.
    const handleChange = (value: Dayjs | null) => {
        field.onChange(value && value.isValid() ? dateToString(value) : '');
    };

    return (
        <DatePicker
            label={label}
            value={field.value}
            onChange={handleChange} // send value to hook form
            inputRef={field.ref} // send input ref, so we can focus on input when error appear
            renderInput={renderDateInput}
            onError={e => {
                if (e?.includes('minDate')) {
                    field.onChange(dateToString(dayjs(minDate)));
                }
            }}
            minDate={minDate}
            disabled={disabled || readOnly || field.disabled}
            {...rest}
        />
    );
}

function renderDateInput(params: TextFieldProps) {
    return <TextField {...params} error={undefined} fullWidth />;
}

const DateRangeField = ({
    control,
    name,
    label,
    readOnly = false,
    disabled = false,
    enableRange = true,
    allowPastDates = false,
}: FieldProps['dateRange']): JSX.Element => {
    const { extraProps } = useCGTLocalizationProviderProps();

    const { getValues, setValue } = useFormContext();
    const [startField, endField] = [`${name}.0`, `${name}.1`];
    const [startLabel, endLabel] = [`${label} start`, `${label} end`];

    const [startDate, endDate] = getValues([startField, endField]);

    useEffect(() => {
        if (enableRange && startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate))) {
            setValue(endField, startDate);
        }
    }, [startDate, endDate, enableRange]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} {...extraProps}>
            <Stack direction="row" gap={1}>
                <DatePickerElement
                    control={control}
                    name={enableRange ? startField : name}
                    label={enableRange ? startLabel : label}
                    // Without the content type flag the field stays constrained to today or later,
                    // as every other date field in the product.
                    minDate={allowPastDates ? undefined : dayjs()}
                    readOnly={readOnly}
                    disabled={disabled}
                />
                {enableRange && (
                    <DatePickerElement
                        control={control}
                        name={endField}
                        label={endLabel}
                        minDate={startDate ? dayjs(startDate) : undefined}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                )}
            </Stack>
        </LocalizationProvider>
    );
};

export default DateRangeField;
